<template>
  <div class="grid grid-cols-4 gap-2">
    <select
      v-model="parts.year"
      @change="onPartsChange"
      :disabled="disabled"
      :class="selectClass(disabled)"
      class="col-span-1 bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-2"
    >
      <option value="">{{ trLabel('Year') }}</option>
      <option v-for="year in yearOptions" :key="`year-${year}`" :value="String(year)">
        {{ year }}
      </option>
    </select>

    <select
      v-model="parts.month"
      @change="onPartsChange"
      :disabled="disabled"
      :class="selectClass(disabled)"
      class="col-span-2 bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-2"
    >
      <option value="">{{ trLabel('Month') }}</option>
      <option v-for="month in monthOptions" :key="`month-${month.value}`" :value="month.value">
        {{ month.label }}
      </option>
    </select>

    <select
      v-model="parts.day"
      @change="onPartsChange"
      :disabled="disabled || !parts.year || !parts.month"
      :class="selectClass(disabled || !parts.year || !parts.month)"
      class="col-span-1 bg-light-gray border border-[#ADAEAE] text-gray text-sm rounded-lg focus:border-[#ADAEAE] focus:ring-transparent outline-none block w-full p-2"
    >
      <option value="">{{ trLabel('Day') }}</option>
      <option v-for="day in dayOptions" :key="`day-${day}`" :value="padDatePart(day)">
        {{ padDatePart(day) }}
      </option>
    </select>
  </div>
</template>

<script>
export default {
  name: 'DatePartsSelect',
  emits: ['input', 'change', 'update:modelValue'],
  props: {
    value: {
      type: String,
      default: null
    },
    modelValue: {
      type: String,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    minYear: {
      type: Number,
      default: 1900
    },
    maxYear: {
      type: Number,
      default: () => new Date().getFullYear()
    }
  },
  data () {
    return {
      parts: {
        year: '',
        month: '',
        day: ''
      }
    };
  },
  watch: {
    inputValue: {
      immediate: true,
      handler (newValue) {
        this.syncFromValue(newValue);
      }
    }
  },
  computed: {
    inputValue () {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    yearOptions () {
      const years = [];
      for (let year = this.maxYear; year >= this.minYear; year--) {
        years.push(year);
      }
      return years;
    },
    monthOptions () {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ];

      return monthNames.map((name, index) => ({
        value: this.padDatePart(index + 1),
        label: this.trLabel(name)
      }));
    },
    dayOptions () {
      if (!this.parts.year || !this.parts.month) {
        return [];
      }

      const daysInMonth = new Date(Number(this.parts.year), Number(this.parts.month), 0).getDate();
      return Array.from({ length: daysInMonth }, (_, index) => index + 1);
    }
  },
  methods: {
    trLabel (key) {
      if (this.$root && typeof this.$root.tr === 'function') {
        return this.$root.tr(key);
      }
      return key;
    },
    selectClass (isReadonly) {
      return {
        error: this.error,
        'readonly-field': isReadonly
      };
    },
    padDatePart (value) {
      return String(value).padStart(2, '0');
    },
    syncFromValue (value) {
      if (!value || typeof value !== 'string') {
        this.parts = { year: '', month: '', day: '' };
        return;
      }

      const [year = '', month = '', day = ''] = value.split('-');
      this.parts = { year, month, day };
    },
    onPartsChange () {
      if (this.parts.year && this.parts.month) {
        const maxDay = new Date(Number(this.parts.year), Number(this.parts.month), 0).getDate();
        if (this.parts.day && Number(this.parts.day) > maxDay) {
          this.parts.day = '';
        }
      }

      const hasAnyPart = Boolean(this.parts.year || this.parts.month || this.parts.day);
      const isComplete = Boolean(this.parts.year && this.parts.month && this.parts.day);

      if (hasAnyPart && !isComplete) {
        return;
      }

      let value = null;
      if (this.parts.year && this.parts.month && this.parts.day) {
        value = `${this.parts.year}-${this.parts.month}-${this.parts.day}`;
      }

      this.$emit('input', value);
      this.$emit('change', value);
      this.$emit('update:modelValue', value);
    }
  }
};
</script>
